import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Card, Flex, Typography } from 'antd';
import type { TableProps } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { ApiError } from '../../lib/api-client';
import { CUSTOMER_HISTORY_SCOPE } from '../../lib/offline-cache';
import type { HistoryList, HistoryPage } from './api';

const { Text, Title } = Typography;

export interface HistorySectionProps<T extends { uid: string }> {
  /** The owning module's query-key prefix, so a merge or the module's own writes refresh it. */
  list: HistoryList;
  customerUid: string;
  fetchPage: (cursor?: string) => Promise<HistoryPage<T>>;
  title: string;
  /** Names the one action that would put a row here. */
  empty: string;
  /** What a role without the module's view permission is told instead. */
  forbidden: string;
  columns: TableProps<T>['columns'];
  /** Where a row opens, on its owning module's page. Null when it has no page. */
  href: (row: T) => string | null;
  renderMobileRow: (row: T) => ReactNode;
}

/**
 * One module's list for one customer, inside a customer-page tab.
 *
 * Mounted only when its tab is opened (antd renders an inactive tab's content
 * on first visit), so a tab fetches nothing until somebody looks at it. A 403
 * is not an error here: the role simply does not see that module, so the
 * section says so and the other sections and tabs carry on.
 */
export function HistorySection<T extends { uid: string }>({
  list,
  customerUid,
  fetchPage,
  title,
  empty,
  forbidden,
  columns,
  href,
  renderMobileRow,
}: HistorySectionProps<T>): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, error, isLoading, isFetching, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    queryKey: [list, CUSTOMER_HISTORY_SCOPE, customerUid],
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
  });

  // A failed first page replaces the list. A failed LATER page keeps what is
  // already on screen (React Query keeps `data`) and says so beside Load more.
  if (error !== null && data === undefined) {
    const denied = error instanceof ApiError && error.status === 403;
    return (
      <Flex vertical gap={8}>
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        <Card size="small">
          {denied ? (
            <Text type="secondary">{forbidden}</Text>
          ) : (
            <Flex vertical gap={8} align="flex-start">
              <Text type="danger">
                {error instanceof ApiError ? error.message : t('customers.history.loadFailed')}
              </Text>
              <Button onClick={() => void refetch()}>{t('common.retry')}</Button>
            </Flex>
          )}
        </Card>
      </Flex>
    );
  }

  const rows = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Flex vertical gap={8}>
      <ListPage<T>
        compact
        title={title}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.uid}
        loading={isLoading || isFetching}
        emptyDescription={empty}
        onRowClick={(row) => {
          const to = href(row);
          if (to !== null) void navigate(to);
        }}
        isRowClickable={(row) => href(row) !== null}
        hasMore={hasNextPage}
        onLoadMore={() => void fetchNextPage()}
        renderMobileRow={renderMobileRow}
      />
      {error !== null && <Text type="danger">{t('customers.history.loadMoreFailed')}</Text>}
    </Flex>
  );
}
